import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { ProjectContext } from '../context/project_context';

const FloatingMediaPlayer = () => {
  const {
    project,
    currentPlaybackTime,
    isPlaying,
    autoScrollEnabled,
    mediaDuration,
    mediaRef,
    handleTimeUpdate,
    handlePlayPause,
    handleSeek,
    toggleAutoScroll,
    setIsPlaying,
    setMediaDuration,
    formatTimeCode
  } = useContext(ProjectContext);

  const progressRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Check if video and/or audio are available (both are optional)
  const hasVideo = !!project?.video_url;
  const hasAudio = !!project?.audio_url;
  const hasBothMedia = hasVideo && hasAudio;
  const hasAnyMedia = hasVideo || hasAudio;

  // State to track which media type is selected (only relevant when both are available)
  // Initialize based on what's available: prefer audio if both exist, otherwise use what's available
  const [selectedMediaType, setSelectedMediaType] = useState(() => {
    if (hasBothMedia) {
      return 'audio'; // Default to audio if both exist
    }
    if (hasAudio) {
      return 'audio';
    }
    if (hasVideo) {
      return 'video';
    }
    return 'audio'; // Fallback
  });

  // Determine media URL and type based on selection
  const getMediaUrl = () => {
    if (hasBothMedia) {
      return selectedMediaType === 'video' ? project.video_url : project.audio_url;
    }
    // If only one type is available, use that one
    if (hasAudio) {
      return project.audio_url;
    }
    if (hasVideo) {
      return project.video_url;
    }
    // Fallback to any available media URL
    return project?.media_url || project?.audio_url || project?.video_url;
  };

  const getMediaType = () => {
    if (hasBothMedia) {
      return selectedMediaType;
    }
    // If only one type is available, return that type
    if (hasAudio && !hasVideo) {
      return 'audio';
    }
    if (hasVideo && !hasAudio) {
      return 'video';
    }
    // Fallback logic
    return project?.media_type || (project?.audio_url ? 'audio' : 'video');
  };

  const mediaUrl = getMediaUrl();
  const mediaType = getMediaType();

  // Keyboard shortcuts for media control
  const handleKeyDown = useCallback((e) => {
    // Shift-based shortcuts
    if (e.shiftKey) {
      // Shift+Space to play/pause
      if (e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        handlePlayPause();
      }
      // Shift+, (< key) to skip back 2s
      else if (e.code === 'Comma') {
        e.preventDefault();
        e.stopPropagation();
        handleSeek(Math.max(0, currentPlaybackTime - 2));
      }
      // Shift+. (> key) to skip forward 2s
      else if (e.code === 'Period') {
        e.preventDefault();
        e.stopPropagation();
        handleSeek(Math.min(mediaDuration, currentPlaybackTime + 2));
      }
      // Shift+Enter to focus on active transcript textbox
      else if (e.code === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        // Dispatch custom event to focus on active transcript
        window.dispatchEvent(new CustomEvent('focusActiveTranscript'));
      }
    }
    
    // Ctrl+Enter to seek player to focused transcript's timecode
    if (e.ctrlKey && !e.shiftKey && e.code === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      // Dispatch custom event to seek to focused transcript
      window.dispatchEvent(new CustomEvent('seekToFocusedTranscript'));
    }
  }, [handlePlayPause, handleSeek, currentPlaybackTime, mediaDuration]);

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Handle media type switch - reset playback time when switching
  const handleMediaTypeSwitch = (newType) => {
    const currentTime = mediaRef.current?.currentTime || 0;
    const wasPlaying = isPlaying;
    
    if (wasPlaying) {
      mediaRef.current?.pause();
    }
    
    setSelectedMediaType(newType);
    
    // After state update, seek to the same time and resume if was playing
    setTimeout(() => {
      if (mediaRef.current) {
        mediaRef.current.currentTime = currentTime;
        if (wasPlaying) {
          mediaRef.current.play();
        }
      }
    }, 100);
  };

  // If no media available (neither audio nor video), show "None Available" message
  if (!hasAnyMedia || !mediaUrl) {
    return (
      <div 
        className="floating-media-player"
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          backgroundColor: '#1a1a2e',
          borderRadius: '12px',
          padding: '16px 24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          minWidth: '300px',
          maxWidth: '90vw',
          textAlign: 'center'
        }}
      >
        <div style={{ 
          color: '#8b8b9e', 
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '20px' }}>🎬</span>
          <span>No video or audio available</span>
        </div>
        <div style={{ 
          color: '#5c5c7a', 
          fontSize: '11px',
          marginTop: '4px'
        }}>
          Upload media in the "Grab Audio" step to enable playback
        </div>
      </div>
    );
  }

  const handleMediaTimeUpdate = () => {
    if (mediaRef.current && !isDragging) {
      handleTimeUpdate(mediaRef.current.currentTime);
    }
  };

  const handleMediaLoadedMetadata = () => {
    if (mediaRef.current) {
      setMediaDuration(mediaRef.current.duration);
    }
  };

  const handleMediaEnded = () => {
    setIsPlaying(false);
  };

  const handleMediaPlay = () => {
    setIsPlaying(true);
  };

  const handleMediaPause = () => {
    setIsPlaying(false);
  };

  const handleProgressClick = (e) => {
    if (progressRef.current && mediaRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * mediaDuration;
      handleSeek(newTime);
    }
  };

  const handleProgressMouseDown = (e) => {
    setIsDragging(true);
    handleProgressClick(e);
  };

  const handleProgressMouseMove = (e) => {
    if (isDragging) {
      handleProgressClick(e);
    }
  };

  const handleProgressMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleProgressMouseMove);
      document.addEventListener('mouseup', handleProgressMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleProgressMouseMove);
      document.removeEventListener('mouseup', handleProgressMouseUp);
    };
  }, [isDragging]);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (mediaRef.current) {
      mediaRef.current.volume = newVolume;
    }
  };

  const handleSkipBack = () => {
    handleSeek(Math.max(0, currentPlaybackTime - 2));
  };

  const handleSkipForward = () => {
    handleSeek(Math.min(mediaDuration, currentPlaybackTime + 2));
  };

  const progressPercentage = mediaDuration > 0 ? (currentPlaybackTime / mediaDuration) * 100 : 0;

  return (
    <div 
      className="floating-media-player"
      style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        backgroundColor: '#1a1a2e',
        borderRadius: '12px',
        padding: isMinimized ? '8px 16px' : '16px 24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        minWidth: isMinimized ? '200px' : (mediaType === 'video' && !isMinimized ? '600px' : '500px'),
        maxWidth: '90vw',
        transition: 'all 0.3s ease'
      }}
    >
      {/* Media element - video is visible when selected, audio is always hidden */}
      {mediaType === 'video' ? (
        <video
          ref={mediaRef}
          src={mediaUrl}
          onTimeUpdate={handleMediaTimeUpdate}
          onLoadedMetadata={handleMediaLoadedMetadata}
          onEnded={handleMediaEnded}
          onPlay={handleMediaPlay}
          onPause={handleMediaPause}
          style={{ 
            display: isMinimized ? 'none' : 'block',
            width: '100%',
            maxHeight: '200px',
            borderRadius: '8px',
            marginBottom: '12px',
            backgroundColor: '#000'
          }}
        />
      ) : (
        <audio
          ref={mediaRef}
          src={mediaUrl}
          onTimeUpdate={handleMediaTimeUpdate}
          onLoadedMetadata={handleMediaLoadedMetadata}
          onEnded={handleMediaEnded}
          onPlay={handleMediaPlay}
          onPause={handleMediaPause}
          style={{ display: 'none' }}
        />
      )}

      {/* Minimize/Expand button */}
      <button
        onClick={() => setIsMinimized(!isMinimized)}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'transparent',
          border: 'none',
          color: '#8b8b9e',
          cursor: 'pointer',
          padding: '4px',
          fontSize: '14px'
        }}
        title={isMinimized ? 'Expand' : 'Minimize'}
      >
        {isMinimized ? '⬆️' : '⬇️'}
      </button>

      {/* Main controls row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Skip back button */}
        <button
          onClick={handleSkipBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#e0e0e0',
            cursor: 'pointer',
            padding: '8px',
            fontSize: '16px',
            display: isMinimized ? 'none' : 'block'
          }}
          title="Skip back 2s"
        >
          ⏪
        </button>

        {/* Play/Pause button */}
        <button
          onClick={handlePlayPause}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '50%',
            width: isMinimized ? '36px' : '48px',
            height: isMinimized ? '36px' : '48px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMinimized ? '16px' : '20px',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
          }}
        >
          {isPlaying ? '⏸️' : '▶️'}
        </button>

        {/* Skip forward button */}
        <button
          onClick={handleSkipForward}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#e0e0e0',
            cursor: 'pointer',
            padding: '8px',
            fontSize: '16px',
            display: isMinimized ? 'none' : 'block'
          }}
          title="Skip forward 2s"
        >
          ⏩
        </button>

        {/* Time display */}
        <div style={{ 
          color: '#e0e0e0', 
          fontFamily: 'monospace',
          fontSize: isMinimized ? '12px' : '14px',
          whiteSpace: 'nowrap'
        }}>
          {formatTimeCode(currentPlaybackTime)}
          {!isMinimized && (
            <span style={{ color: '#8b8b9e' }}> / {formatTimeCode(mediaDuration)}</span>
          )}
        </div>

        {/* Progress bar */}
        {!isMinimized && (
          <div
            ref={progressRef}
            onClick={handleProgressClick}
            onMouseDown={handleProgressMouseDown}
            style={{
              flex: 1,
              height: '8px',
              backgroundColor: '#3d3d5c',
              borderRadius: '4px',
              cursor: 'pointer',
              position: 'relative',
              minWidth: '100px'
            }}
          >
            {/* Progress fill */}
            <div
              style={{
                width: `${progressPercentage}%`,
                height: '100%',
                backgroundColor: '#667eea',
                borderRadius: '4px',
                transition: isDragging ? 'none' : 'width 0.1s ease'
              }}
            />
            {/* Seek handle */}
            <div
              style={{
                position: 'absolute',
                left: `${progressPercentage}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '14px',
                height: '14px',
                backgroundColor: '#fff',
                borderRadius: '50%',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                opacity: isDragging ? 1 : 0,
                transition: 'opacity 0.2s ease'
              }}
            />
          </div>
        )}

        {/* Volume control */}
        {!isMinimized && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#8b8b9e', fontSize: '14px' }}>🔊</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              style={{
                width: '60px',
                cursor: 'pointer',
                accentColor: '#667eea'
              }}
            />
          </div>
        )}

        {/* Auto-scroll toggle */}
        {!isMinimized && (
          <button
            onClick={toggleAutoScroll}
            style={{
              background: autoScrollEnabled ? 'rgba(102, 126, 234, 0.3)' : 'transparent',
              border: `1px solid ${autoScrollEnabled ? '#667eea' : '#3d3d5c'}`,
              borderRadius: '6px',
              color: autoScrollEnabled ? '#667eea' : '#8b8b9e',
              cursor: 'pointer',
              padding: '6px 12px',
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}
            title={autoScrollEnabled ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}
          >
            {autoScrollEnabled ? '📜 Auto' : '📜 Manual'}
          </button>
        )}
      </div>

      {/* Media type indicator and selector */}
      {!isMinimized && (
        <div style={{
          marginTop: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Media type selector (only shown when both video and audio are available) */}
          {hasBothMedia ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                color: '#8b8b9e', 
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Source:
              </span>
              <div style={{
                display: 'flex',
                backgroundColor: '#2d2d44',
                borderRadius: '6px',
                padding: '2px'
              }}>
                <button
                  onClick={() => handleMediaTypeSwitch('audio')}
                  style={{
                    background: selectedMediaType === 'audio' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    color: selectedMediaType === 'audio' ? '#fff' : '#8b8b9e',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    fontSize: '11px',
                    transition: 'all 0.2s ease'
                  }}
                  title="Play audio track"
                >
                  🎵 Audio
                </button>
                <button
                  onClick={() => handleMediaTypeSwitch('video')}
                  style={{
                    background: selectedMediaType === 'video' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    color: selectedMediaType === 'video' ? '#fff' : '#8b8b9e',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    fontSize: '11px',
                    transition: 'all 0.2s ease'
                  }}
                  title="Play video track"
                >
                  🎬 Video
                </button>
              </div>
            </div>
          ) : (
            <span style={{ 
              color: '#8b8b9e', 
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {mediaType === 'video' ? '🎬 Video' : '🎵 Audio'} Player
            </span>
          )}
          
          {/* Help button and project name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setShowShortcuts(!showShortcuts)}
              style={{ 
                color: showShortcuts ? '#667eea' : '#5c5c7a', 
                fontSize: '11px',
                backgroundColor: showShortcuts ? 'rgba(102, 126, 234, 0.2)' : '#2d2d44',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'pointer',
                border: showShortcuts ? '1px solid #667eea' : '1px solid transparent',
                transition: 'all 0.2s ease'
              }}
            >
              ⌨️ Shortcuts
            </button>
            <span style={{ 
              color: '#5c5c7a', 
              fontSize: '11px' 
            }}>
              {project?.name || 'Untitled Project'}
            </span>
          </div>
        </div>
      )}

      {/* Keyboard shortcuts panel */}
      {!isMinimized && showShortcuts && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#2d2d44',
          borderRadius: '8px',
          border: '1px solid #3d3d5c'
        }}>
          <div style={{ 
            color: '#e0e0e0', 
            fontSize: '12px', 
            fontWeight: 'bold',
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>⌨️ Keyboard Shortcuts</span>
            <button
              onClick={() => setShowShortcuts(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#8b8b9e',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '0'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '6px 16px',
            fontSize: '11px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#8b8b9e' }}>Play / Pause</span>
              <code style={{ 
                backgroundColor: '#1a1a2e', 
                padding: '2px 6px', 
                borderRadius: '4px',
                color: '#667eea',
                fontSize: '10px'
              }}>Shift + Space</code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#8b8b9e' }}>Skip back 2s</span>
              <code style={{ 
                backgroundColor: '#1a1a2e', 
                padding: '2px 6px', 
                borderRadius: '4px',
                color: '#667eea',
                fontSize: '10px'
              }}>Shift + &lt;</code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#8b8b9e' }}>Skip forward 2s</span>
              <code style={{ 
                backgroundColor: '#1a1a2e', 
                padding: '2px 6px', 
                borderRadius: '4px',
                color: '#667eea',
                fontSize: '10px'
              }}>Shift + &gt;</code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#8b8b9e' }}>Focus active row</span>
              <code style={{ 
                backgroundColor: '#1a1a2e', 
                padding: '2px 6px', 
                borderRadius: '4px',
                color: '#667eea',
                fontSize: '10px'
              }}>Shift + Enter</code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gridColumn: 'span 2' }}>
              <span style={{ color: '#8b8b9e' }}>Go to focused row's timecode</span>
              <code style={{ 
                backgroundColor: '#1a1a2e', 
                padding: '2px 6px', 
                borderRadius: '4px',
                color: '#667eea',
                fontSize: '10px'
              }}>Ctrl + Enter</code>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingMediaPlayer;
